/**
 * MSSQL JSON-path extraction from cursor context.
 *
 * Given the buffer text and a cursor offset inside a comparison RHS string
 * literal (`WHERE JSON_VALUE(metadata, '$.status') = '|'`), detects whether
 * the LHS of the comparison is a MSSQL JSON-extract expression and returns
 * the normalized canonical JSONPath (`$.status`) so the enum-value resolver
 * can look up path-keyed hints.
 *
 * Supported MSSQL JSON-extract forms (v1):
 *   JSON_VALUE(col, '$.a.b')          → canonical `$.a.b`  (scalar)
 *   JSON_QUERY(col, '$.a.b')          → canonical `$.a.b`  (object/array)
 *   JSON_VALUE(col, 'lax $.a.b')      → canonical `$.a.b`  (lax mode prefix)
 *   JSON_VALUE(col, 'strict $.a.b')   → canonical `$.a.b`  (strict mode prefix)
 *
 * Path arg is ALWAYS the second arg. Per MSSQL docs the path may optionally
 * begin with the `lax` or `strict` mode keyword (case-insensitive) followed
 * by whitespace, then the `$` root. MSSQL has no shorthand operators (no PG
 * `->>` analogue).
 *
 * Intentionally NOT supported in v1:
 *   - JSON_PATH_EXISTS (returns BIT — enum-value LHS would be `BIT = '...'`,
 *     never a typed string column).
 *   - JSON_MODIFY (writer, not a reader; comparison LHS doesn't make sense).
 *   - OPENJSON / CROSS APPLY (these produce row sets, not scalars).
 *
 * Pure text scanner — no tree-sitter dependency. Mirrors the backward-scan
 * pattern of `extractJsonPathFromCursor.pgsql.ts`.
 */

import { normalizeJsonPath } from '@/features/workspace/common/utils/jsonFilterSql';
import {
  skipValueOperand,
  COMPARISON_OPERATORS,
  endsWithAt,
} from '../context/comparisonScanHelpers';

export type ExtractJsonPathResult = {
  /** Already passed through normalizeJsonPath. */
  readonly canonicalPath: string;
  /** The column name (un-bracketed / unquoted) from the LHS. */
  readonly column: string;
};

/**
 * Returns the normalized JSON path when the cursor sits at the comparison
 * RHS of a MSSQL JSON-extract expression, or `null` otherwise.
 *
 * `column` is the column name from the resolved table column binding — used
 * to verify the extract expression targets the same column so we don't offer
 * completions for the wrong column.
 */
export function extractJsonPathFromCursorMssql(args: {
  readonly text: string;
  readonly cursorOffset: number;
  /** Omit to accept whichever column the extract expression names — the
   *  scan itself never depends on it (the JSON-chain enum fallback scans
   *  once, then matches the returned `column` against the snapshot). */
  readonly column?: string;
}): ExtractJsonPathResult | null {
  const { text, cursorOffset, column } = args;
  if (cursorOffset < 0 || cursorOffset > text.length) return null;

  // Walk backward from the cursor past the in-progress string literal to land
  // on the comparison operator boundary (exclusive end of LHS).
  const opBoundary = findComparisonOpEndBefore(text, cursorOffset);
  if (opBoundary === null) return null;

  // The token immediately before the comparison operator should be the
  // closing `)` of a JSON_VALUE / JSON_QUERY call. Skip whitespace.
  let i = opBoundary - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i--;
  if (i < 0 || text[i] !== ')') return null;

  // Parse the JSON_VALUE / JSON_QUERY call ending at the `)`.
  const extracted = parseExtractCallAt(text, i);
  if (!extracted) return null;

  // Verify the column matches (case-insensitive).
  if (column !== undefined && extracted.column.toLowerCase() !== column.toLowerCase()) return null;

  return {
    canonicalPath: extracted.canonicalPath,
    column: extracted.column,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Comparison operator boundary scan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Walk back from `offset` over the optional in-progress RHS string literal,
 * using `skipValueOperand` (which handles all the SQL string-scanning edge
 * cases) and return the exclusive-end index of the LHS expression
 * (i.e., `opBoundary` where `text.slice(0, opBoundary)` ends with the
 * comparison operator). Returns `null` when no recognized comparison operator
 * precedes the cursor's operand position.
 */
function findComparisonOpEndBefore(text: string, offset: number): number | null {
  const i = skipValueOperand(text, offset - 1);
  if (i < 0) return null;

  const opEnd = i + 1;
  for (const op of COMPARISON_OPERATORS) {
    if (endsWithAt(text, opEnd, op)) {
      return opEnd - op.length; // index of first char of the LHS (exclusive end)
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// JSON_VALUE / JSON_QUERY call parser
// ─────────────────────────────────────────────────────────────────────────────

type ExtractResult = { column: string; canonicalPath: string };

/**
 * Parse a MSSQL JSON_VALUE / JSON_QUERY call whose closing `)` sits at
 * `closeParenIdx`. Returns `null` when the pattern isn't recognized.
 *
 * Shape:  FUNC ( <colRef> , '<path>' )
 *
 * `<colRef>` may be a bare identifier (`metadata`), bracket-quoted
 * (`[metadata]`), double-quoted (`"metadata"`), or alias-qualified
 * (`t.metadata`, `t.[Payload]`). We extract only the trailing identifier
 * (the column name); alias resolution lives in the resolver.
 */
function parseExtractCallAt(text: string, closeParenIdx: number): ExtractResult | null {
  if (text[closeParenIdx] !== ')') return null;

  // The path literal must be the last argument: backward scan finds its
  // closing `'` immediately before the `)` (whitespace tolerant).
  let j = closeParenIdx - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0 || text[j] !== "'") return null;

  const pathLit = readQuotedStringEndingAt(text, j);
  if (!pathLit) return null;

  // Step before the opening quote of the path literal.
  let k = pathLit.openIdx - 1;
  while (k >= 0 && /\s/.test(text[k]!)) k--;
  if (k < 0 || text[k] !== ',') return null;

  // Step before the comma — this should be the column-ref expression.
  k--;
  while (k >= 0 && /\s/.test(text[k]!)) k--;

  // Read the column ref (trailing identifier of a possibly alias-qualified
  // form). `colRef` is the un-bracketed / unquoted column name.
  const colInfo = readColumnRefBefore(text, k);
  if (!colInfo) return null;

  // Step before the column ref to the opening `(`.
  let m = colInfo.startIdx - 1;
  while (m >= 0 && /\s/.test(text[m]!)) m--;
  if (m < 0 || text[m] !== '(') return null;

  // Step before the `(` to the function name.
  m--;
  while (m >= 0 && /\s/.test(text[m]!)) m--;

  const fname = readBareIdentBefore(text, m);
  if (!fname) return null;

  const lower = fname.toLowerCase();
  if (lower !== 'json_value' && lower !== 'json_query') return null;

  // The path must start with `$` to be a valid JSONPath. (Empty/whitespace
  // means the user typed a malformed call — we return null and let the
  // generic path-hint detector handle empty-string completion.)
  //
  // MSSQL allows an optional `lax` or `strict` mode prefix before `$`
  // (case-insensitive, separated by whitespace). Strip it before the gate so
  // canonical paths like `$.status` flow through `normalizeJsonPath` cleanly.
  let rawPath = pathLit.content.trim();
  rawPath = rawPath.replace(/^(?:lax|strict)\s+/i, '');
  if (rawPath === '' || !rawPath.startsWith('$')) return null;

  return {
    column: colInfo.column,
    canonicalPath: normalizeJsonPath(rawPath),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// String literal + identifier helpers
// ─────────────────────────────────────────────────────────────────────────────

type QuotedString = {
  content: string;
  /** Index of the opening `'`. */
  openIdx: number;
};

function readQuotedStringEndingAt(text: string, end: number): QuotedString | null {
  if (text[end] !== "'") return null;
  let i = end - 1;
  let content = '';
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === "'") {
      if (i > 0 && text[i - 1] === "'") {
        content = "'" + content;
        i -= 2;
        continue;
      }
      return { content, openIdx: i };
    }
    if (ch === '\n') return null;
    content = ch + content;
    i--;
  }
  return null;
}

/**
 * Read a bare identifier whose last char is at `end`. Accepts `A-Za-z0-9_`
 * but rejects pure digit runs and leading-digit identifiers.
 */
function readBareIdentBefore(text: string, end: number): string | null {
  let j = end;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;
  const idEnd = j + 1;
  while (j >= 0 && /[A-Za-z0-9_]/.test(text[j]!)) j--;
  if (j + 1 === idEnd) return null;
  const id = text.slice(j + 1, idEnd);
  if (id.length === 0 || /^\d/.test(id)) return null;
  return id;
}

type ColumnRefInfo = {
  /** Un-bracketed / unquoted column name (last identifier in a dotted form). */
  column: string;
  /** Start index of the column ref expression in `text`. */
  startIdx: number;
};

/**
 * Read a MSSQL column-ref expression whose last char is at `end`. Accepts:
 *   - bare identifier: `metadata`
 *   - bracket-quoted:  `[metadata]` (with `]]` escaped inside)
 *   - double-quoted:   `"metadata"` (with `""` escaped inside)
 *   - alias-qualified: `t.metadata`, `t.[Payload]`, `[t].[Payload]`
 *
 * Returns the trailing identifier (column part, un-bracketed) plus the
 * start index of the whole ref so the caller can step before it.
 */
function readColumnRefBefore(text: string, end: number): ColumnRefInfo | null {
  let i = end;
  while (i >= 0 && /\s/.test(text[i]!)) i--;
  if (i < 0) return null;

  // Read the trailing identifier segment.
  const last = readOneIdentSegmentEndingAt(text, i);
  if (!last) return null;

  let startIdx = last.startIdx;
  const columnName = last.name;

  // Optional dot-qualifier(s) — `t.col`, `s.t.col`. Walk back consuming
  // `.<segment>` until no more dots. We only need to track `startIdx`; the
  // returned `column` is the LAST segment (the column part).
  let cursor = last.startIdx - 1;
  while (cursor >= 0 && /\s/.test(text[cursor]!)) cursor--;
  while (cursor >= 0 && text[cursor] === '.') {
    cursor--;
    while (cursor >= 0 && /\s/.test(text[cursor]!)) cursor--;
    const seg = readOneIdentSegmentEndingAt(text, cursor);
    if (!seg) break;
    startIdx = seg.startIdx;
    cursor = seg.startIdx - 1;
    while (cursor >= 0 && /\s/.test(text[cursor]!)) cursor--;
  }

  return { column: columnName, startIdx };
}

type IdentSegment = { name: string; startIdx: number };

/**
 * Read ONE identifier segment ending at `end`. Returns its un-quoted name
 * and the start index in `text`. Supports bare, `[...]`, and `"..."` forms.
 */
function readOneIdentSegmentEndingAt(text: string, end: number): IdentSegment | null {
  if (end < 0) return null;
  const ch = text[end]!;

  if (ch === ']') {
    // Bracket-quoted — scan back to matching `[`, honouring `]]` escape.
    let i = end - 1;
    let content = '';
    while (i >= 0) {
      const c = text[i]!;
      if (c === ']' && text[i - 1] === ']') {
        // `]]` escapes — represents a literal `]`. In source order this
        // pair is two consecutive ]'s. Reading backward we see the first
        // `]` then the second; both belong to the content. We've already
        // read one `]` as a delim though — but `[Order]]Details]` reads as:
        // close `]`, then `]]` pair, then content. To match `unquoteIdentifier`
        // semantics (Order]Details), we treat `]]` as an escaped `]`.
        content = ']' + content;
        i -= 2;
        continue;
      }
      if (c === '[') {
        return { name: content, startIdx: i };
      }
      if (c === '\n') return null;
      content = c + content;
      i--;
    }
    return null;
  }

  if (ch === '"') {
    // Double-quoted — scan back to opening `"`, honouring `""` escape.
    let i = end - 1;
    let content = '';
    while (i >= 0) {
      const c = text[i]!;
      if (c === '"') {
        if (i > 0 && text[i - 1] === '"') {
          content = '"' + content;
          i -= 2;
          continue;
        }
        return { name: content, startIdx: i };
      }
      if (c === '\n') return null;
      content = c + content;
      i--;
    }
    return null;
  }

  // Bare identifier.
  let j = end;
  while (j >= 0 && /[A-Za-z0-9_]/.test(text[j]!)) j--;
  const idStart = j + 1;
  if (idStart > end) return null;
  const name = text.slice(idStart, end + 1);
  if (name.length === 0 || /^\d/.test(name)) return null;
  return { name, startIdx: idStart };
}
